import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { Button, Col, Flex, Form, Grid, Input, List, Popconfirm, Row, Table, Tag, Tooltip, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { EditOutlined } from "@ant-design/icons";
import { TypeLembreteDaViagem } from "../../../../../Models/Domain/Viagem/Lembrete";

import dayjs from "dayjs"
import { ResponsiveAntdTable } from "../../../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../../../Components/Conjunto";
import { Ordenar } from "../../../../../Utils/Ordenacao";
import { Formatacoes } from "../../../../../Utils/Formatacao";
import InputDate from "../../../../../Components/Base/Input/InputDate";

interface IFormularioDeLembretes {
    lembretesAPI: [TypeLembreteDaViagem[], React.Dispatch<SetStateAction<TypeLembreteDaViagem[]>>];
    permitirEdicao?: boolean;
}

export const FormularioDeLembretes: React.FC<IFormularioDeLembretes> = (props) => {

    const tamanhoDaTela = Grid.useBreakpoint();
    const [lembretes, setLembretes] = props.lembretesAPI;
    const [form] = useForm<TypeLembreteDaViagem>();

    const [IDDoItemEmEdicao, setIDDoItemEmEdicao] = useState<number | null>();
    const [IDDeAdicao, setIDDeAdicao] = useState<number>(-1);

    const estaEditando = useMemo(() => IDDoItemEmEdicao != null, [IDDoItemEmEdicao]);

    const onFinish = (valores: TypeLembreteDaViagem) => {
        valores.ativo = true;
        valores.feito = false;

        if (estaEditando) {
            valores.codigo = IDDoItemEmEdicao!;
            setLembretes((prev) =>
                prev.map((t) => (t.codigo === IDDoItemEmEdicao ? valores : t))
            );
            setIDDoItemEmEdicao(null);
        } else {
            valores.codigo = IDDeAdicao;
            setIDDeAdicao(IDDeAdicao - 1);
            setLembretes((prev) => [...prev, valores]);
        }

        form.resetFields();
    };

    return (
        <React.Fragment>
            <Flex vertical style={{ width: "100%" }}>
                {IDDoItemEmEdicao != null && (
                    <Row gutter={16}>
                        <Col xs={24}>
                            {(() => {
                                const it = lembretes.find((v) => v.codigo === IDDoItemEmEdicao);
                                if (it == null) return;
                                return (
                                    <Typography.Paragraph>
                                        <Tag color="geekblue-inverse">
                                            <EditOutlined /> {it.descricao}
                                        </Tag>
                                    </Typography.Paragraph>
                                );
                            })()}
                        </Col>
                    </Row>
                )}

                <Form form={form} onFinish={onFinish} autoComplete="off" layout="vertical" validateTrigger="onBlur">
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <Form.Item<TypeLembreteDaViagem>
                                name="descricao"
                                label="Descrição"
                                rules={[{ required: true, message: "Por favor, informe a descrição!" }]}
                            >
                                <Input id="descricao-lembrete" placeholder="Informe a descrição" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={5}>
                            <Form.Item<TypeLembreteDaViagem>
                                name="quando"
                                label="Quando"
                                rules={[{ required: true, message: "Por favor, informe a data!" }]}
                            >
                                <InputDate style={{ width: "100%" }} placeholder="Selecione" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={4} style={{ display: "flex", gap: "16px" }}>
                            <Form.Item label={<></>}>
                                <Button type="primary" key="submit" htmlType="submit" style={{ flex: 1 }}>
                                    {estaEditando ? "Salvar" : "Adicionar"}
                                </Button>
                            </Form.Item>

                            {estaEditando && (
                                <Form.Item label={<></>}>
                                    <Button
                                        type="default"
                                        danger
                                        style={{ flex: 1 }}
                                        onClick={() => {
                                            setIDDoItemEmEdicao(null);
                                            form.resetFields();
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                </Form.Item>
                            )}
                        </Col>
                    </Row>
                </Form>

                <Row>
                    <Col xs={24}>
                        <div style={{ paddingTop: 16 }}>
                            <ResponsiveAntdTable<TypeLembreteDaViagem>
                                bordered
                                rowKey={(k) => k.codigo!}
                                locale={{ emptyText: 'Nenhum lembrete adicionado.' }}
                                addTitleOn="lg"
                                dataSource={ lembretes.filter((l) => l.ativo != false) }
                                columns={[
                                    {
                                        title: "Quando",
                                        dataIndex: "quando",
                                        key: "lembretes-codigo",
                                        breakpoints: { xs: 8, lg: 4 },
                                        sorter: (a, b) => Ordenar.Data(a.quando, b.quando),
                                        render: (_, record) => {
                                            return Formatacoes.paraData(record.quando)?.toLocaleDateString()
                                        }
                                    },
                                    {
                                        title: "Feito",
                                        dataIndex: ["feito"],
                                        key: "lembretes-feito",
                                        breakpoints: { xs: 8, lg: 2 },
                                        sorter: (a, b) => Ordenar.Booleano(a.feito, b.feito),
                                        render: (v: boolean) => {
                                            return v ?
                                                <Tag style={{ width: "min-content" }} color="green"> Sim </Tag>
                                                :
                                                <Tag style={{ width: "min-content" }} color="red"> Não </Tag>
                                        }
                                    },
                                    {
                                        title: "Descrição",
                                        dataIndex: ["descricao"],
                                        key: "lembretes-descricao",
                                        sorter: (a, b) => Ordenar.String(a.descricao, b.descricao),
                                        breakpoints: { xs: 8, lg: 13 },
                                    },

                                    {
                                        title: "Ações",
                                        key: "lembretes-acoes",
                                        noResponsiveTitle: true,
                                        breakpoints: { xs: 24, lg: 5 },
                                        render: (_, item) => {
                                            return <Conjunto
                                                itens={
                                                    (() => {
                                                        const alternar = (
                                                            <Button
                                                                type="default"
                                                                size="small"
                                                                onClick={() => {
                                                                    setLembretes((prev) =>
                                                                        prev.map((e) =>
                                                                            e.codigo === item.codigo ? { ...e, feito: !e.feito } : e
                                                                        )
                                                                    );
                                                                }}
                                                            >
                                                                {item.feito? "Restaurar": "Finalizar"}
                                                            </Button>
                                                        );

                                                        const editar = (
                                                            <Button
                                                                disabled={estaEditando}
                                                                type="primary"
                                                                size="small"
                                                                onClick={() => {
                                                                    setIDDoItemEmEdicao(item.codigo);
                                                                    form.setFieldsValue({
                                                                        ...item,
                                                                        quando: (() => { let l = Formatacoes.paraData(item.quando); return l ? dayjs(l) : undefined })()
                                                                    });
                                                                }}
                                                            >
                                                                Editar
                                                            </Button>
                                                        );

                                                        const excluir = (
                                                            <Popconfirm 
                                                                title='Atenção'
                                                                description='Tem certeza que deseja excluir este registro?'
                                                                okText='Confirmar'
                                                                cancelText='Cancelar'
                                                                onConfirm={() => {
                                                                    setLembretes((prev) =>
                                                                        prev.map((e) =>
                                                                            e.codigo === item.codigo ? { ...e, ativo: false } : e
                                                                        )
                                                                    );
                                                                }}
                                                            >
                                                                <Button
                                                                    disabled={estaEditando}
                                                                    type="default"
                                                                    danger
                                                                    size="small"
                                                                >
                                                                    Excluir
                                                                </Button>
                                                            </Popconfirm>
                                                        );

                                                        if (props.permitirEdicao) {
                                                            return [alternar, editar, excluir];
                                                        }

                                                        return [alternar, excluir];
                                                    })()
                                                }
                                            />
                                        }
                                    },
                                ]}
                            />

                        </div>
                    </Col>
                </Row>
            </Flex>
        </React.Fragment>
    );
};
